// ─── Featmap Excel 导出工具 ────────────────────────────────────────────────────
// 使用 SheetJS (xlsx) 纯前端生成，无需后端 API
// 生成两个 Sheet：
//   Sheet1 - 故事地图总览（Milestone 为列，Workflow/SubWorkflow 为行）
//   Sheet2 - 功能明细清单（所有 Feature 平铺，含全部字段）

import * as XLSX from 'xlsx'
import { IMilestone } from '../store/milestones/types'
import { IWorkflow } from '../store/workflows/types'
import { ISubWorkflow } from '../store/subworkflows/types'
import { IFeature } from '../store/features/types'
import { filterFeaturesOnMilestoneAndSubWorkflow } from '../store/features/selectors'
import { getSubWorkflowByWorkflow } from '../store/subworkflows/selectors'
import { filterWorkflowsOnProject } from '../store/workflows/selectors'
import { filterMilestonesOnProject } from '../store/milestones/selectors'

export interface ExportData {
  projectTitle: string
  projectId: string
  milestones: IMilestone[]
  workflows: IWorkflow[]
  subWorkflows: ISubWorkflow[]
  features: IFeature[]
}

// 状态映射为中文
const STATUS_MAP: Record<string, string> = {
  'open':   '进行中',
  'closed': '已完成',
}
function statusLabel(s: string) {
  return STATUS_MAP[s] || s
}

// ── Sheet1：故事地图总览 ────────────────────────────────────────────────────────
function buildOverviewSheet(data: ExportData): XLSX.WorkSheet {
  const { projectId, milestones, workflows, subWorkflows, features } = data

  const projectMilestones = filterMilestonesOnProject(milestones, projectId)
  const projectWorkflows  = filterWorkflowsOnProject(workflows, projectId)

  // 表头：第一列为"活动层级"，后续列为各 Milestone 标题
  const headerRow = ['目标 / 活动', ...projectMilestones.map(m => m.title)]
  const rows: string[][] = [headerRow]

  // 每个 Workflow 下展开其 SubWorkflows
  projectWorkflows.forEach(w => {
    const subs = getSubWorkflowByWorkflow(subWorkflows, w.id)
    subs.forEach((sw, swIdx) => {
      // 第一列：第一行显示 "目标名 > 活动名"，后续 sub 只显示活动名
      const rowLabel = swIdx === 0 ? `${w.title} › ${sw.title}` : `  › ${sw.title}`
      const row: string[] = [rowLabel]

      // 每个 Milestone 列：列出该交叉点的功能标题（换行连接）
      projectMilestones.forEach(m => {
        const feats = filterFeaturesOnMilestoneAndSubWorkflow(features, m.id, sw.id)
        row.push(feats.map(f => f.title).join('\n'))
      })
      rows.push(row)
    })
  })

  const ws = XLSX.utils.aoa_to_sheet(rows)

  // 设置列宽
  ws['!cols'] = [
    { wch: 30 },
    ...projectMilestones.map(() => ({ wch: 24 }))
  ]

  return ws
}

// ── Sheet2：功能明细清单 ────────────────────────────────────────────────────────
function buildDetailSheet(data: ExportData): XLSX.WorkSheet {
  const { projectId, milestones, workflows, subWorkflows, features } = data

  const projectMilestones = filterMilestonesOnProject(milestones, projectId)
  const projectWorkflows  = filterWorkflowsOnProject(workflows, projectId)

  const header = ['版本（Release）', '目标（Goal）', '活动（Activity）', '功能（Feature）', '描述', '状态', '估算', '标注']
  const rows: (string | number)[][] = [header]

  projectMilestones.forEach(m => {
    projectWorkflows.forEach(w => {
      getSubWorkflowByWorkflow(subWorkflows, w.id).forEach(sw => {
        filterFeaturesOnMilestoneAndSubWorkflow(features, m.id, sw.id).forEach(f => {
          rows.push([
            m.title,
            w.title,
            sw.title,
            f.title,
            f.description || '',
            statusLabel(f.status),
            f.estimate || 0,
            f.annotations || '',
          ])
        })
      })
    })
  })

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!cols'] = [
    { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 28 },
    { wch: 40 }, { wch: 10 }, { wch: 8  }, { wch: 20 },
  ]
  return ws
}

// ── 主导出函数 ─────────────────────────────────────────────────────────────────
export function exportProjectToExcel(data: ExportData) {
  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(wb, buildOverviewSheet(data), '故事地图总览')
  XLSX.utils.book_append_sheet(wb, buildDetailSheet(data),   '功能明细清单')

  const filename = `${data.projectTitle}-故事地图.xlsx`
  XLSX.writeFile(wb, filename)
}
